import BaseModel from './BaseModel'
import LocationProduct from './LocationProduct'
import Organization from './Organization'

export default class Product extends BaseModel {

  static entity = 'products'
  static primaryKey = 'ID'

  declare ID: number
  declare organizationID: number
  declare name: string
  declare verticalType: string
  declare productType: string
  declare entryHoursBeforeEvent: number
  declare exitHoursAfterEvent: number
  declare daysSun: boolean
  declare daysMon: boolean
  declare daysTue: boolean
  declare daysWed: boolean
  declare daysThu: boolean
  declare daysFri: boolean
  declare daysSat: boolean
  declare firstStartTime: string | null
  declare lastStartTime: string | null
  declare endTime: string | null
  declare locationProducts: LocationProduct[]
  declare organization: Organization

  static fields() {

    return {

      // fields
      ID: this.number(null),
      organizationID: this.number(null),
      name: this.string(''),
      verticalType: this.string(''), // Verticals
      productType: this.string(''), // ProductTypes
      entryHoursBeforeEvent: this.number(2),
      exitHoursAfterEvent: this.number(2),
      daysSun: this.boolean(false),
      daysMon: this.boolean(false),
      daysTue: this.boolean(false),
      daysWed: this.boolean(false),
      daysThu: this.boolean(false),
      daysFri: this.boolean(false),
      daysSat: this.boolean(false),
      firstStartTime: this.string(null).nullable(), // in hh:mm:ss format
      lastStartTime: this.string(null).nullable(), // in hh:mm:ss format
      endTime: this.string(null).nullable(), // in hh:mm:ss format

      // relationships
      locationProducts: this.hasMany(LocationProduct, 'productID'),
      organization: this.belongsTo(Organization, 'organizationID'),

    }

  }

  /**
   * Returns an array of weekdays on which this product is enabled (Sunday = 0).
   */
  get enabledWeekdays(): Array<Number> {
    const weekdays = []
    if (this.daysSun) { weekdays.push(0) }
    if (this.daysMon) { weekdays.push(1) }
    if (this.daysTue) { weekdays.push(2) }
    if (this.daysWed) { weekdays.push(3) }
    if (this.daysThu) { weekdays.push(4) }
    if (this.daysFri) { weekdays.push(5) }
    if (this.daysSat) { weekdays.push(6) }
    return weekdays    
  }

  isEnabledOnWeekday(weekdayIndex: number): boolean {

    if (weekdayIndex === 0) { return this.daysSun }
    if (weekdayIndex === 1) { return this.daysMon }
    if (weekdayIndex === 2) { return this.daysTue }
    if (weekdayIndex === 3) { return this.daysWed }
    if (weekdayIndex === 4) { return this.daysThu }
    if (weekdayIndex === 5) { return this.daysFri }
    if (weekdayIndex === 6) { return this.daysSat }
    return false

  }

  isEnabledOnAtLeastOneWeekday(weekdayIndexes: Array<number>): boolean {
    return weekdayIndexes.some(weekdayIndex => this.isEnabledOnWeekday(weekdayIndex))
  }

  /**
   * API methods
   */

  static fetchAll() {
    return this.api().get('/products/get_all', {
      dataTransformer: ({ data }) => {
        return data.products.map(
          (product: any) => Product._convertProductFromAPI(product)
        )
      }
    })
  }

  static saveNew(payload: UpsertProductRequest) {
    const massagedPayload = Product._massagePayloadForUpsert(payload)
    return this.api()
      .post('/products/create', massagedPayload, {
        dataTransformer: ({ data }) => {
          return Product._convertProductFromAPI(data)
        },
      })
      .catch((error) => {
        let errorMsg = error.response.data
        throw errorMsg
      })
  }

  static saveExisting(productID: number, payload: UpsertProductRequest) {
    const massagedPayload = Product._massagePayloadForUpsert(payload)
    return this.api()
      .put('/products/product/' + productID, massagedPayload, {
        dataTransformer: ({ data }) => {
          return Product._convertProductFromAPI(data)
        },
      })
      .catch((error) => {
        let errorMsg = error.response.data
        throw errorMsg
      })
  }

  static destroy(productID: number) {
    return this.api()
      .delete('/products/product/' + productID, {
        delete: productID,
      })
      .catch((error) => {
        let errorMsg = error.response.data
        throw errorMsg
      })
  }

  static _convertProductFromAPI(product: any) {

    // Temporary hack to work around a bug in the API: Some endpoints have the
    // exitHoursAfterEvent field misnamed as exitHoursBeforeEvent. Feel free
    // to remove this once the bug has been fixed.
    // https://app.asana.com/0/1202228339672381/1202248747110789
    if ('exitHoursBeforeEvent' in product) {
      product.exitHoursAfterEvent = product.exitHoursBeforeEvent
      delete product.exitHoursBeforeEvent
    }

    // The API returns the weekday booleans nested inside an object. Flatten
    // these, so we can store them in the Vuex model.
    product.daysSun = product.days.sun
    product.daysMon = product.days.mon
    product.daysTue = product.days.tue
    product.daysWed = product.days.wed
    product.daysThu = product.days.thu
    product.daysFri = product.days.fri
    product.daysSat = product.days.sat
    delete product.days

    return product  

  }

  static _massagePayloadForUpsert(payload: any) {

    // Temporary hack to work around API bug: https://app.asana.com/0/1202228339672381/1202248747110789
    // Feel free to remove this once the bug has been fixed.

    const massagedPayload = { ...payload } // clone, so we aren't modifying the passed object

    if ('entryHoursBeforeEvent' in massagedPayload) {
      massagedPayload.entry_hours_before_event = massagedPayload.entryHoursBeforeEvent
    }
    if ('exitHoursAfterEvent' in massagedPayload) {
      massagedPayload.exit_hours_after_event = massagedPayload.exitHoursAfterEvent
    }    

    return massagedPayload

  }

}

export interface UpsertProductRequest {
  organizationID: number
  verticalType: string
  productType: string
  name: string
  days: {[key: string]: boolean}
  entryHoursBeforeEvent?: number
  exitHoursAfterEvent?: number
  firstStartTime?: null | string
  lastStartTime?: null | string
  endTime?: null | string
}
